'use client'

import { useEffect } from 'react'
import { Cell, Icon, Label, Spacer, Validation } from '@vinted/web-ui'
import { Pencil24, Plus24, Telephone24 } from '@vinted/monochrome-icons'
import { InView } from 'react-intersection-observer'

import { ErrorItem } from '@marketplace-web/core-api/api-client-util'
import { useBreakpoint } from '@marketplace-web/breakpoints/breakpoints-feature'
import { scrollToElementById } from '@marketplace-web/browser/browser-interaction-util'
import { useTracking } from '@marketplace-web/observability/event-tracker-data'
import { useTranslate } from '@marketplace-web/i18n/i18n-feature'

import {
  buyerViewAddContactDetailsEvent,
  sellerViewAddContactDetailsEvent,
} from '@marketplace-web/complaints/complaints-data'
import {
  FieldName,
  PhoneRequirement,
  ShippingContactType,
} from '@marketplace-web/shipping/shipping-contact-data'

import { scrollIntoViewById } from '../../utils'

type Props = {
  type: ShippingContactType
  translationPrefix: string
  phoneNumber: string | null | undefined
  phoneRequirement: PhoneRequirement
  transactionId: number
  isDisabled: boolean
  isCompleted: boolean
  isCheckout?: boolean
  error: ErrorItem | null
  onClick: () => void
}

const ShippingContact = ({
  type,
  translationPrefix,
  phoneRequirement,
  transactionId,
  phoneNumber,
  isDisabled,
  isCompleted,
  isCheckout,
  error,
  onClick,
}: Props) => {
  const { track } = useTracking()
  const { tabletsUp } = useBreakpoint()
  const translate = useTranslate(translationPrefix)

  useEffect(() => {
    if (!error) return

    if (!isCheckout) {
      scrollIntoViewById(FieldName.ShippingContact)

      return
    }
    scrollToElementById(FieldName.ShippingContact)
  }, [error, isCheckout])

  const handleWaypointEnter = (inView: boolean) => {
    if (!inView) return

    if (!transactionId) return

    if (isCheckout) {
      track(buyerViewAddContactDetailsEvent({ transactionId, screen: 'checkout' }))

      return
    }

    track(sellerViewAddContactDetailsEvent({ transactionId, screen: 'get_shipping_label' }))
  }

  const renderPrefix = (isEmpty: boolean) => {
    if (isCheckout) return null
    if (type === ShippingContactType.ShippingLabel) return null
    if (isEmpty && type === ShippingContactType.DigitalLabel) return null

    return (
      <Icon name={Telephone24} color="greyscale-level-2" testId="shipping-contact-telephone-icon" />
    )
  }

  const renderContent = () => {
    const isEmpty = !isCompleted || (phoneRequirement === PhoneRequirement.Optional && !phoneNumber)
    const title = isEmpty ? translate('title') : phoneNumber
    const iconName = isEmpty ? Plus24 : Pencil24
    const prefix = renderPrefix(isEmpty)
    const styling = isCheckout ? 'wide' : undefined
    const color = isEmpty ? 'greyscale-level-1' : 'greyscale-level-2'

    return (
      <Cell
        type="navigating"
        styling={styling}
        title={title}
        prefix={prefix}
        suffix={
          <Icon
            name={iconName}
            color={color}
            testId={`shipping-contact-${iconName.Title}-icon`}
            aria={{
              'aria-hidden': 'true',
            }}
          />
        }
        onClick={onClick}
        validation={error && <Validation text={error.value} theme="warning" />}
        testId="shipping-contact-details"
      />
    )
  }

  const renderCheckoutShippingContact = () => (
    <>
      <Spacer size="x-large" />
      <Cell styling="tight">
        <Label type="leading" styling={tabletsUp ? 'wide' : 'default'} text={translate('label')} />
        {renderContent()}
      </Cell>
    </>
  )

  const renderShippingContact = () => (
    <>
      <Label text={translate('label')} type="stacked" />
      {renderContent()}
    </>
  )

  if (isDisabled || !transactionId) return null

  return (
    <InView id={FieldName.ShippingContact} onChange={handleWaypointEnter}>
      {isCheckout ? renderCheckoutShippingContact() : renderShippingContact()}
    </InView>
  )
}

export default ShippingContact
